// api frontend sidebar with module filtering
import React, { useState, useContext, useEffect } from 'react';
import MyContext from './MyContext';
import { Layout, Menu } from 'antd';
import {
  LineChartOutlined,
  PieChartOutlined,
  DatabaseOutlined,
  MinusSquareOutlined,
  TableOutlined,
  FundOutlined
} from '@ant-design/icons';

const { Sider } = Layout;
const { SubMenu } = Menu;

function Sidebar() {
  const {selectedKey, setSelectedKey, setIsModalVisible, selectedModule, setSelectedModule} = useContext(MyContext);
  const [collapsed, setCollapsed] = useState(false);
  const [purchasedModuleKey, setPurchasedModuleKey] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch purchased module from backend/session on component mount
  useEffect(() => {
    fetchPurchasedModule();
  }, []);

  const fetchPurchasedModule = async () => {
    try {
      // Call your Django backend to get the purchased module from session
      const response = await fetch('/api/get-purchased-module/', {
        credentials: 'include' // Include session cookies
      });
      const data = await response.json();
      
      if (data.module_key) {
        setPurchasedModuleKey(data.module_key);
        setSelectedKey(data.module_key); // Auto-select the purchased module
        
        // Determine the parent module based on the key
        const parentModule = getParentModule(data.module_key);
        setSelectedModule(parentModule);
      }
    } catch (error) {
      console.error('Error fetching purchased module:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine parent module from key
  const getParentModule = (key) => {
    const moduleGroups = {
      'break-even': ['1', '2', '3'],
      'forecasting': ['4', '5', '6', '12'],
      'inventory': ['8', '13', '23', '9', '19', '20', '10', '17', '18'],
      'decision-making': ['21', '22', '11'],
      'output-rates': ['14', '15', '16']
    };

    for (const [parent, children] of Object.entries(moduleGroups)) {
      if (children.includes(key)) {
        return parent;
      }
    }
    return null;
  };

  // Check if a menu item should be visible
  const isModuleVisible = (moduleKey) => {
    if (!purchasedModuleKey) return false; // Hide all if no purchase
    return moduleKey === purchasedModuleKey; // Show only purchased module
  };

  // Check if a submenu should be visible (has at least one visible child)
  const isSubMenuVisible = (childKeys) => {
    if (!purchasedModuleKey) return false;
    return childKeys.includes(purchasedModuleKey);
  };

  const handleMenuClick = (e) => {
    console.log("Sidebar selected key:", e.key);
    setSelectedKey(e.key);
    if (e.key === "1" || e.key === "2" || e.key === "11" || e.key === "3" || e.key === "8" || 
        e.key === "9" || e.key === "10" || e.key === "12" || e.key === "6" || e.key === "13" || 
        e.key === "14" || e.key === "15" || e.key === "20" || e.key === "21" || e.key === "23" || 
        e.key === "17") {
      setIsModalVisible(true);
    }
  };

  const MAIN_MODULES = {
    'break-even': 'Break Even Analysis',
    'forecasting': 'Forecasting', 
    'inventory': 'Inventory Management',
    'decision-making': 'Decision Making',
    'output-rates': 'Measuring Output Rates'
  };

  const handleSubMenuClick = (key) => {
    console.log("Main module selected:", key);
    setSelectedModule(key);
    setSelectedKey(key);
    console.log("Module name:", MAIN_MODULES[key]);
  };

  if (loading) {
    return (
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} theme="light" width={300}>
        <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>
      </Sider>
    );
  }

  return (
    <Sider
      collapsible 
      collapsed={collapsed}
      onCollapse={setCollapsed}
      theme="light"
      width={300}
    >
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        onClick={handleMenuClick}
        style={{ height: '100%', borderRight: 0 }}
      >
        {/* Title section */}
        <Menu.Item key="title" icon={<MinusSquareOutlined />}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', height: '50px' }}>
              <p style={{ fontWeight: 'bold', color: 'darkblue', fontSize: '14px' }}>
                Module Tree
              </p>
            </div>
          )}
        </Menu.Item>

        {/* Break Even Analysis submenu */}
        {isSubMenuVisible(['1', '2', '3']) && (
          <SubMenu
            key="break-even"
            title="Break Even Analysis"
            icon={<LineChartOutlined />}
            onTitleClick={() => handleSubMenuClick('break-even')}
          >
            {isModuleVisible('1') && <Menu.Item key="1">Crossover/Cost-Volume Analysis</Menu.Item>}
            {isModuleVisible('2') && <Menu.Item key="2">Breakeven Analysis (Cost)</Menu.Item>}
            {isModuleVisible('3') && <Menu.Item key="3">Multiproduct Breakeven Analysis</Menu.Item>}
          </SubMenu>
        )}

        {/* Forecasting submenu */}
        {isSubMenuVisible(['4', '5', '6', '12']) && (
          <SubMenu
            key="forecasting"
            title="Forecasting"
            icon={<PieChartOutlined />}
            onTitleClick={() => handleSubMenuClick('forecasting')}
          >
            {isModuleVisible('4') && <Menu.Item key="4">Time Series Analysis</Menu.Item>}
            {isModuleVisible('5') && <Menu.Item key="5">Least Squares Simple and Multiple Regression</Menu.Item>}
            {isModuleVisible('6') && <Menu.Item key="6">Regression Projector</Menu.Item>}
            {isModuleVisible('12') && <Menu.Item key="12">Error Analysis</Menu.Item>}
          </SubMenu>
        )}

        {/* Inventory Management submenu */}
        {isSubMenuVisible(['8', '13', '23', '9', '19', '20', '10', '17', '18']) && (
          <SubMenu
            key="inventory"
            title="Inventory Management"
            icon={<DatabaseOutlined />}
            onTitleClick={() => handleSubMenuClick('inventory')}
          >
            {isModuleVisible('8') && <Menu.Item key="8">Economic Order Quantity (EOQ) Model</Menu.Item>}
            {isModuleVisible('13') && <Menu.Item key="13">Economic Production Lot Size Model</Menu.Item>}
            {isModuleVisible('23') && <Menu.Item key="23">Quality Discount(EOQ) Model</Menu.Item>}
            {isModuleVisible('9') && <Menu.Item key="9">ABC Analysis</Menu.Item>}
            {isModuleVisible('19') && <Menu.Item key="19">Reorder Point / Safety Stock (Discrete Distribution)</Menu.Item>}
            {isModuleVisible('20') && <Menu.Item key="20">Reorder Point / Safety Stock (Normal Distribution)</Menu.Item>}
            {isModuleVisible('10') && <Menu.Item key="10">Kanban Computation</Menu.Item>}
            {isModuleVisible('17') && <Menu.Item key="17">Single Period Inventory (Discrete Distribution)</Menu.Item>}
            {isModuleVisible('18') && <Menu.Item key="18">Single Period Inventory (Normal Distribution)</Menu.Item>}
          </SubMenu>
        )}

        {/* Decision Making submenu */}
        {isSubMenuVisible(['21', '22', '11']) && (
          <SubMenu
            key="decision-making"
            title="Decision Making"
            icon={<TableOutlined />}
            onTitleClick={() => handleSubMenuClick('decision-making')}
          >
            {isModuleVisible('21') && <Menu.Item key="21">Decision Table</Menu.Item>}
            {isModuleVisible('22') && <Menu.Item key="22">Decision Trees</Menu.Item>}
            {isModuleVisible('11') && <Menu.Item key="11">Create Preference Matrix</Menu.Item>}
          </SubMenu>
        )}

        {/* Measuring Output Rates submenu */}
        {isSubMenuVisible(['14', '15', '16']) && (
          <SubMenu
            key="output-rates"
            title="Measuring Output Rates"
            icon={<FundOutlined />}
            onTitleClick={() => handleSubMenuClick('output-rates')}
          >
            {isModuleVisible('14') && <Menu.Item key="14">Time Study</Menu.Item>}
            {isModuleVisible('15') && <Menu.Item key="15">Sample Size for Time Studies</Menu.Item>}
            {isModuleVisible('16') && <Menu.Item key="16">Sample Size for Work Sampling</Menu.Item>}
          </SubMenu>
        )}
      </Menu>

      {/* Display current purchased module info */}
      {!collapsed && purchasedModuleKey && (
        <div style={{ 
          padding: '10px', 
          borderTop: '1px solid #f0f0f0', 
          fontSize: '12px', 
          color: '#666',
          backgroundColor: '#f9f9f9'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Your Module:</div>
          <div>{getModuleName(purchasedModuleKey)}</div>
        </div>
      )}
    </Sider>
  );
}

// Helper function to get module name by key
function getModuleName(key) {
  const moduleNames = {
    '1': 'Crossover/Cost-Volume Analysis',
    '2': 'Breakeven Analysis (Cost)',
    '3': 'Multiproduct Breakeven Analysis',
    '4': 'Time Series Analysis',
    '5': 'Least Squares Regression',
    '6': 'Regression Projector',
    '12': 'Error Analysis',
    '8': 'Economic Order Quantity Model',
    '13': 'Economic Production Lot Size',
    '23': 'Quality Discount Model',
    '9': 'ABC Analysis',
    '19': 'Reorder Point (Discrete)',
    '20': 'Reorder Point (Normal)',
    '10': 'Kanban Computation',
    '17': 'Single Period Inventory (Discrete)',
    '18': 'Single Period Inventory (Normal)',
    '21': 'Decision Table',
    '22': 'Decision Trees',
    '11': 'Create Preference Matrix',
    '14': 'Time Study',
    '15': 'Sample Size for Time Studies',
    '16': 'Sample Size for Work Sampling'
  };
  
  return moduleNames[key] || 'Unknown Module';
}

export default Sidebar;